/**
 * Student answer slots inside a Math blank's slot expression (PBI 538086).
 *
 * An author writes a fixed expression (the slot expression, e.g. `√▢ < √5 < √▢`) and marks any number of
 * slots in it for the student. A slot is a MathLive prompt — `\placeholder[<slotId>]{}` — which is not
 * the same thing as MathLive's bare `\placeholder{}`: the bare one is a navigation hole a template key
 * leaves behind (and `normalizeMathLatex` strips it), while a prompt carries an id and is the only part
 * of a read-only field the student can type into.
 *
 * The author sees and edits the SOLVED exercise — each slot holding its correct answer — but the stored
 * template always holds EMPTY slots: it reaches the student, so it must never carry an answer. Each
 * slot's correct answer is its own row in the blank's sensitive `inputCorrectOptions`, tagged with the
 * slot id; `mergeMathSlotAnswers` puts them back for the author. Each slot's student answer is its own
 * entry in the answer record, under `mathSlotResponseKey(clozeId, slotId)`. A slot therefore grades and
 * scores like a blank of its own, which is what gives every slot its own outline and share of the score.
 */

import { expressionNeedsCorrectAnswer, type MathPolicyExpression } from './mathPolicyIds';
import { normalizeMathLatex } from './normalizeMathLatex';

const SLOT_KEY_SEPARATOR = ':';

export type ParsedMathSlot = {
  id: string;
  body: string;
  // Offsets into the parsed string: the whole `\placeholder[…]…{…}` command.
  start: number;
  end: number;
};

const PLACEHOLDER = '\\placeholder';

function isLetter(char: string | undefined) {
  return char !== undefined && /[a-zA-Z]/.test(char);
}

/** The brace group opening at `open`, as [inner text, index past the closing brace], or null. */
function readBraceGroup(source: string, open: number): [string, number] | null {
  if (source[open] !== '{') return null;
  let depth = 0;
  for (let cursor = open; cursor < source.length; cursor++) {
    const char = source[cursor];
    if (char === '\\') {
      cursor++;
      continue;
    }
    if (char === '{') depth++;
    else if (char === '}') {
      depth--;
      if (depth === 0) return [source.slice(open + 1, cursor), cursor + 1];
    }
  }
  return null;
}

/**
 * The top-level slots of an expression, in reading order. A slot's first `[…]` argument is its id; any
 * further ones are the state mathlive appends (`[correct]`, `[locked]`). A bare `\placeholder{}` has
 * no id and is not a slot. Slots nested inside a slot are left in its `body`.
 */
export function parseMathSlots(latex: string | undefined): ParsedMathSlot[] {
  if (!latex) return [];
  const slots: ParsedMathSlot[] = [];
  let index = 0;

  while (index < latex.length) {
    const start = latex.indexOf(PLACEHOLDER, index);
    if (start === -1) break;
    let cursor = start + PLACEHOLDER.length;
    if (isLetter(latex[cursor])) {
      // A longer command that merely starts with `\placeholder`.
      index = cursor;
      continue;
    }

    const args: string[] = [];
    const skipSpaces = () => {
      while (latex[cursor] === ' ') cursor++;
    };
    skipSpaces();
    while (latex[cursor] === '[') {
      const close = latex.indexOf(']', cursor);
      if (close === -1) break;
      args.push(latex.slice(cursor + 1, close).trim());
      cursor = close + 1;
      skipSpaces();
    }
    const group = readBraceGroup(latex, cursor);
    if (!group) {
      index = cursor;
      continue;
    }

    const [body, end] = group;
    if (args[0]) slots.push({ id: args[0], body, start, end });
    // A bare hole may hold a slot, so look inside it; a slot's own nested slots stay in its body.
    index = args[0] ? end : cursor + 1;
  }

  return slots;
}

/** The ids of an expression's slots, in reading order. */
export function mathSlotIds(template: string | undefined): string[] {
  return parseMathSlots(template).map(slot => slot.id);
}

export function hasMathSlots(template: string | undefined): template is string {
  return parseMathSlots(template).length > 0;
}

/** The response key of one slot of a Cloze blank. */
export function mathSlotResponseKey(clozeId: string, slotId: string) {
  return `${clozeId}${SLOT_KEY_SEPARATOR}${slotId}`;
}

/** The blank (and slot, for a slotted Math blank) a response key addresses. */
export function resolveClozeResponseKey(key: string): { clozeId: string; slotId?: string } {
  const separator = key.indexOf(SLOT_KEY_SEPARATOR);
  return separator === -1 ? { clozeId: key } : { clozeId: key.slice(0, separator), slotId: key.slice(separator + 1) };
}

export type ClozeOptionLike =
  | { type?: unknown; answerFormat?: unknown; mathSlotExpression?: unknown; mathPolicies?: unknown }
  | undefined;

// The Cloze blank type that can hold slots. The literal, not the enum: the enum's schema module imports
// this one.
const MATH_BLANK_TYPE = 'Math';

/**
 * A blank's slots, in reading order: none unless it is a Math blank with a slot expression. The one place
 * that decides whether a blank takes an answer per slot, so the Player, grading and Studio cannot
 * disagree about it.
 */
export function clozeBlankSlotIds(option: ClozeOptionLike): string[] {
  if (option?.type !== MATH_BLANK_TYPE || typeof option.mathSlotExpression !== 'string') return [];
  return mathSlotIds(option.mathSlotExpression);
}

/**
 * The grading rules for one answer of a Math blank: a slot's when `slotId` is given, the blank's own
 * otherwise. Today every slot takes its blank's rules (`mathPolicies`). This is the seam for rules per
 * slot: they would be stored beside the blank's, and only this function would change, falling back to
 * the blank's rules for a slot without its own, so existing content needs no migration. Grading and
 * Studio both ask it, so they cannot disagree about which rules grade a slot.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- `slotId` is the seam's input; see above.
export function clozeSlotPolicies(option: ClozeOptionLike, slotId?: string): MathPolicyExpression | undefined {
  return option?.mathPolicies as MathPolicyExpression | undefined;
}

/** Whether a blank's answer (a slot's, with `slotId`) is graded against a stored correct answer. */
export function clozeSlotNeedsAnswer(option: ClozeOptionLike, slotId?: string): boolean {
  return expressionNeedsCorrectAnswer(clozeSlotPolicies(option, slotId));
}

/**
 * Every answer a Cloze question takes, as response keys (the keys of the student answer record): a blank's own id, or one key per slot of a
 * slotted Math blank. Grading, scoring and "fully answered" all count over this one list, so they can
 * never disagree about how many answers a question has.
 */
export function clozeResponseKeys(clozeOptions: Record<string, ClozeOptionLike> | undefined): string[] {
  return Object.entries(clozeOptions ?? {}).flatMap(([clozeId, option]) => clozeOptionResponseKeys(clozeId, option));
}

export function clozeOptionResponseKeys(clozeId: string, option: ClozeOptionLike): string[] {
  const slotIds = clozeBlankSlotIds(option);
  return slotIds.length > 0 ? slotIds.map(slotId => mathSlotResponseKey(clozeId, slotId)) : [clozeId];
}

/**
 * A slotted blank's one result, like every other blank's: correct only when every slot is, wrong as soon as
 * one slot is, and undecided while any slot has not been graded.
 */
export function combineMathSlotResults(results: Array<boolean | undefined>): boolean | undefined {
  if (results.some(result => result === false)) return false;
  return results.length > 0 && results.every(result => result === true) ? true : undefined;
}

/**
 * Whether every slot of an expression is empty. The expression reaches the student in every mode, so an
 * answer written into a slot (by a future writer, an import, a hand-fixed document) would reach them too,
 * and the author would never see it: their field puts each slot's own answer in its place.
 */
export function hasOnlyEmptySlots(expression: string): boolean {
  return parseMathSlots(expression).every(slot => normalizeMathLatex(slot.body) === '');
}
